import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { PublishJobDto, PublishJobStepOutcome } from '@course-authoring/shared-types';
import { useLanguageContext } from '../../hooks/useLanguageContext';
import { PublishJobStatus, PublishJobRunLog, PublishJobStatusWarning } from '@course-authoring/ui-blocks';
import type { PublishJobPopoverPlacement } from '@course-authoring/ui-blocks';

function deriveThemeTranslationOutcomes(job: PublishJobDto, targets: string[]): PublishJobStepOutcome[] {
  const outcomes = job.stepResults.filter((outcome) => outcome.step === 'translation');
  const combined: PublishJobStepOutcome[] = [];

  for (const locale of targets) {
    const localeOutcomes = outcomes.filter((outcome) => outcome.locale === locale);
    const failure = localeOutcomes.find((outcome) => outcome.status === 'failed');
    if (failure) {
      combined.push(failure);
      continue;
    }

    const latestFor = (seam: string) => localeOutcomes.filter((outcome) => outcome.seam === seam).at(-1);
    const greeting = latestFor('greeting');
    const intro = latestFor('intro');
    if (!greeting || !intro) continue;

    const statuses = [greeting.status, intro.status];
    combined.push({
      step: 'translation',
      locale,
      status: statuses.includes('generated') ? 'generated'
        : statuses.includes('reused') ? 'reused'
          : 'skipped',
    });
  }

  combined.push(...outcomes.filter((outcome) => !outcome.locale && outcome.status === 'failed'));
  return combined;
}

export function ThemePreparationStatus({
  job, ready, isSaving = false, inherited, error, dirty, retrying, onRetry,
}: {
  job: PublishJobDto | null;
  ready: boolean;
  isSaving?: boolean;
  inherited: boolean;
  error: string | null;
  dirty: boolean;
  retrying: boolean;
  onRetry: () => void;
}): React.ReactElement | null {
  const { languageOptions, selectedLocale } = useLanguageContext();
  const id = `theme-preparation-${useId()}`;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement | null>(null);
  const previousIdentityRef = useRef<string | null>(null);
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<PublishJobPopoverPlacement | null>(null);
  const visibleJob = job && (job.status !== 'completed' || ready) ? job : null;
  const jobIdentity = job ? `${job.id}:${job.attempt}` : null;
  const assignPanelRef = useCallback((node: HTMLElement | null) => {
    panelRef.current = node;
    node?.focus();
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [selectedLocale]);

  useEffect(() => {
    if (isSaving) setOpen(false);
  }, [isSaving]);

  useEffect(() => {
    if (previousIdentityRef.current !== null && previousIdentityRef.current !== jobIdentity) setOpen(false);
    previousIdentityRef.current = jobIdentity;
  }, [jobIdentity]);

  useEffect(() => {
    if (!open || !visibleJob) return undefined;
    const updatePlacement = () => {
      const anchor = triggerRef.current?.getBoundingClientRect();
      if (!anchor) return;
      const margin = 8;
      const width = Math.min(400, Math.max(0, window.innerWidth - margin * 2));
      const left = Math.min(Math.max(margin, anchor.left), Math.max(margin, window.innerWidth - width - margin));
      const below = window.innerHeight - anchor.bottom - margin;
      const above = anchor.top - margin;
      setPlacement(below >= Math.min(240, above) || below >= above
        ? { left, width, top: anchor.bottom + 4, maxHeight: Math.max(120, below) }
        : { left, width, bottom: window.innerHeight - anchor.top + 4, maxHeight: Math.max(120, above) });
    };
    updatePlacement();
    const closeOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!triggerRef.current?.parentElement?.contains(target) && !panelRef.current?.contains(target)) setOpen(false);
    };
    const closeEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    };
    window.addEventListener('scroll', updatePlacement, true);
    window.addEventListener('resize', updatePlacement);
    document.addEventListener('mousedown', closeOutside);
    document.addEventListener('keydown', closeEscape);
    return () => {
      window.removeEventListener('scroll', updatePlacement, true);
      window.removeEventListener('resize', updatePlacement);
      document.removeEventListener('mousedown', closeOutside);
      document.removeEventListener('keydown', closeEscape);
    };
  }, [open, visibleJob?.id, visibleJob?.attempt]);

  if (!visibleJob) return isSaving ? <PublishJobStatus job={null} ready languageOptions={languageOptions}
    isRunLogOpen={false} onToggleRunLog={() => {}} isRetrying={false}
    isMutationBlocked isDirty={dirty} isSaving /> : null;
  const outcomes = visibleJob.stepResults.filter((outcome) => outcome.step === 'translation');
  const targets = [...new Set(visibleJob.result.targets?.translation ?? outcomes.flatMap((outcome) => outcome.locale ? [outcome.locale] : []))];
  const presentationJob: PublishJobDto = {
    ...visibleJob,
    stepResults: deriveThemeTranslationOutcomes(visibleJob, targets),
    result: { ...visibleJob.result, targets: { translation: targets } },
  };
  return <>
    <PublishJobStatus job={presentationJob} ready languageOptions={languageOptions}
      isRunLogOpen={open} onToggleRunLog={() => setOpen((current) => !current)}
      controls={id} triggerRef={triggerRef} accessiblePrefix="Assistant translation preparation"
      onRetry={inherited ? undefined : onRetry} isRetrying={retrying}
      isMutationBlocked={false} isDirty={dirty} isSaving={isSaving} showDraftState={false} />
    {open && !isSaving && placement && <PublishJobRunLog job={presentationJob} languageOptions={languageOptions}
      steps={['translation']} id={id} popover={{ placement, panelRef: assignPanelRef }}
      onClose={() => { setOpen(false); triggerRef.current?.focus(); }}
      copy={{ preparing: 'Preparing translations', ready: 'Translations are ready',
        failureDescription: 'Settings are saved and remain editable. Retry preparation starts translation again.' }}
      notice={inherited ? 'Base theme preparation · account settings remain editable. This preparation belongs to the base theme.' : undefined} />}
    {error && <PublishJobStatusWarning message={error} />}
  </>;
}
